import type { PayoutResponse } from "@/modules/dashboard/dispersions/services/paymentsPayout.service";
import type { PayinRuntimeEntity } from "@/modules/dashboard/payins/services/paymentsPayin.service";
import { adaptPayinRow, adaptPayoutRow } from "@/modules/dashboard/payments-activity/utils/activityAdapters";
import type { NativeSwap, SwapListEntity } from "@/modules/dashboard/swaps/services/swap.service";
import { formatRail } from "@/shared/utils/railDisplay";

import {
  SWAP_NON_TERMINAL,
  SWAP_STATUS_BUCKET,
  type SwapRuntimeStatus,
} from "../constants/paymentsLedger.constants";
import { LedgerOperationType, type LedgerOperationCandidate } from "../types/paymentsLedger.types";

/**
 * Runtime entity → explorer row. Payins and payouts go through the payments-activity adapters so the status
 * bucket, terminality and headline-amount precedence have a single definition across both surfaces; only the
 * fields the activity row does not carry (the organization, the corridor caption) are read from the entity.
 */
export function payoutToCandidate(payout: PayoutResponse): LedgerOperationCandidate {
  const row = adaptPayoutRow(payout);
  return {
    kind: "payouts",
    ledgerType: LedgerOperationType.PAYOUT,
    id: payout.id,
    organizationId: payout.organization_id,
    status: row.status,
    bucket: row.bucket,
    isNonTerminal: row.isNonTerminal,
    amount: row.amount,
    currency: row.currency,
    subtitle: `${payout.country} · ${formatRail(payout.rail)}`,
    createdAt: payout.created_at,
  };
}

export function payinToCandidate(payin: PayinRuntimeEntity): LedgerOperationCandidate {
  const row = adaptPayinRow(payin);
  return {
    kind: "payins",
    ledgerType: LedgerOperationType.PAYIN,
    id: payin.id,
    organizationId: payin.organization_id,
    status: row.status,
    bucket: row.bucket,
    isNonTerminal: row.isNonTerminal,
    amount: row.amount,
    currency: row.currency,
    subtitle: `${payin.country} · ${formatRail(payin.rail)}`,
    createdAt: payin.created_at,
  };
}

/**
 * A swap has no corridor and its own three-value status vocabulary, so it gets its own mapping. The headline
 * is `amount_received` in the destination currency — what the organization ended up holding.
 */
export function swapToCandidate(swap: SwapListEntity): LedgerOperationCandidate {
  return {
    kind: "swaps",
    ledgerType: LedgerOperationType.SWAP,
    id: swap.id,
    organizationId: swap.organization_id,
    status: swap.status,
    bucket: SWAP_STATUS_BUCKET[swap.status as SwapRuntimeStatus] ?? "pending",
    isNonTerminal: SWAP_NON_TERMINAL.has(swap.status),
    amount: swap.amount_received,
    currency: swap.destination_currency,
    subtitle: `${swap.origin_currency} → ${swap.destination_currency}`,
    createdAt: swap.created_at,
  };
}

/**
 * `GET /internal/payments/swaps/:id` answers the `{ swap: … }` summary shape, which — unlike the list row —
 * carries no `organization_id`: it was resolved from the caller's own query. The organization therefore comes
 * from whoever asked for the detail (the ledger's own account scope, on a deep link).
 */
export function nativeSwapToCandidate(swap: NativeSwap, organizationId: string): LedgerOperationCandidate {
  return {
    kind: "swaps",
    ledgerType: LedgerOperationType.SWAP,
    id: swap.id,
    organizationId,
    status: swap.status,
    bucket: SWAP_STATUS_BUCKET[swap.status as SwapRuntimeStatus] ?? "pending",
    isNonTerminal: SWAP_NON_TERMINAL.has(swap.status),
    amount: swap.summary.amount_received,
    currency: swap.summary.destination_currency,
    subtitle: `${swap.summary.origin_currency} → ${swap.summary.destination_currency}`,
    createdAt: swap.created_at,
  };
}
